import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faArrowUpRightFromSquare, faStopwatch } from '@fortawesome/pro-regular-svg-icons';
import { TranslatePipe } from '@ngx-translate/core';
import { type SignalTypeOf } from '@reconcept-platform/shared/data-access';
import { ProgressBarComponent } from '@reconcept-platform/shared/ui/core/progress-bar/progress-bar.component';
import { LabelComponent } from '@reconcept-platform/shared/ui/standalone/label';
import { BorderboxComponent, type BorderBoxState, TileComponent } from '@reconcept-platform/shared/ui/standalone/tile';
import {
  type ContentPreviewSize,
  type ContentTilePreview,
  ContentTilePreviewComponent,
} from '../content-tile-preview/content-tile-preview.component';

export interface ContentItem {
  title: string;
  description?: string;
  type?: string;
  isExternalLink?: boolean;
  isVideo?: boolean;
  isProcessing?: boolean;
  minutes?: number;
  progress?: number;
  progressBarColor?: SignalTypeOf<ProgressBarComponent['color']>;
  preview?: ContentTilePreview;
}

@Component({
  selector: 'rcp-content-tile',
  template: `
    @if (previewSize() === 'full-width') {
      <rcp-borderbox [clickable]="clickable()" class="full-width__container">
        <rcp-content-tile-preview
          [size]="previewSize()"
          [isVideo]="content().isVideo ?? false"
          [preview]="content().preview"
        />

        <div class="full-width__overlay flex flex-column">
          <div class="full-width__overlay__title">{{ content().title }}</div>
          @if (content().description; as description) {
            <div class="full-width__overlay__description">{{ description }}</div>
          }
        </div>
      </rcp-borderbox>
    } @else {
      <rcp-tile
        [clickable]="clickable()"
        [label]="content().title"
        [description]="content().description"
        [draggable]="draggable()"
        [state]="state()"
      >
        <div leading-detail>
          <rcp-content-tile-preview
            [size]="previewSize()"
            [isVideo]="content().isVideo ?? false"
            [preview]="content().preview"
          />
        </div>
        <div class="content" bottom-description>
          <div class="content__detail">
            @if (!content().description && content().type; as type) {
              <div class="content__type">{{ type }}</div>
            }
            @if (content().minutes) {
              <rcp-label color="neutral" [icon]="faStopwatch">
                {{ content().minutes }} {{ 'qz-minutes-zm' | translate }}
              </rcp-label>
            }
          </div>
          @if (typeof content().progress === 'number' || content().isProcessing) {
            <rcp-progress-bar
              [progress]="content().progress"
              [color]="content().progressBarColor ?? 'default'"
              class="content__progress"
            />
          }
        </div>

        <div trailing-detail>
          <ng-content select="[actions]"></ng-content>
          @if (content().isExternalLink && !draggable()) {
            <fa-icon class="content__external-link" [icon]="faArrowUpRightFromSquare" trailing-detail />
          }
        </div>
        <div secondary-action>
          <ng-content select="[secondary-action]"></ng-content>
        </div>
      </rcp-tile>
    }
  `,
  styleUrl: './content-tile.component.scss',
  imports: [
    TileComponent,
    ContentTilePreviewComponent,
    LabelComponent,
    TranslatePipe,
    FontAwesomeModule,
    ProgressBarComponent,
    BorderboxComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ContentTileComponent {
  readonly content = input.required<ContentItem>();
  readonly draggable = input(false);
  readonly clickable = input(true);
  readonly previewSize = input<ContentPreviewSize>('large');
  readonly state = input<BorderBoxState>('default');

  protected readonly faStopwatch = faStopwatch;
  protected readonly faArrowUpRightFromSquare = faArrowUpRightFromSquare;
}
